import { useEffect, useState } from "react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";
import { motion, AnimatePresence } from "framer-motion";
import { Pencil, Save, Send, ExternalLink, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import emailjs from '@emailjs/browser';
type ProgressData = Tables<"progress_tracking">;

interface ProgressTrackingProps {
  familyName?: string;
  googleDriveUrl?: string;
  password?: string;
}

export const ProgressTracking = ({ 
  familyName = "Wolken Family",
  googleDriveUrl = "https://drive.google.com/drive/folders/1gGrMYoLgzKuc2YKdcEM1Ve3nxj0db6ff?usp=sharing",
  password: _password
}: ProgressTrackingProps = {}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [progress, setProgress] = useState<ProgressData | null>(null);
  const [feedback, setFeedback] = useState("");
  const [feedbackEmail, setFeedbackEmail] = useState("");
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);
  const {
    toast
  } = useToast();
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyName]);

  const fetchProgress = async () => {
    const identifier = familyName || "General";
    const { data, error } = await supabase.functions.invoke('progress-api', {
      body: { action: 'get', familyName: identifier }
    });

    if (error || !data?.progress) {
      console.error('Error fetching progress:', error);
      toast({
        title: "Error",
        description: "Failed to load progress data",
        variant: "destructive"
      });
      return;
    }

    setProgress(data.progress as ProgressData);
  };
  const handleEditClick = async () => {
    const identifier = familyName || "General";
    const { data, error } = await supabase.functions.invoke('progress-api', {
      body: { action: 'verify', familyName: identifier, password }
    });

    if (error || !data?.ok) {
      toast({
        title: "Incorrect Password",
        description: "Please try again",
        variant: "destructive"
      });
      return;
    }

    setEditPassword(password);
    setIsEditing(true);
    setPassword("");
    setIsEditOpen(false);
  };
  const handleSave = async () => {
    if (!progress) return;

    const identifier = familyName || "General";
    const { data, error } = await supabase.functions.invoke('progress-api', {
      body: {
        action: 'save',
        familyName: identifier,
        password: editPassword,
        progress: {
          order_submitted: !!progress.order_submitted,
          planning_form_complete: !!progress.planning_form_complete,
          started_draft_1: !!progress.started_draft_1,
          completed_draft_1: !!progress.completed_draft_1,
          completed_draft_2: !!progress.completed_draft_2,
          completed_final_draft: !!progress.completed_final_draft,
          payment_received: !!progress.payment_received
        }
      }
    });

    if (error || !data?.progress) {
      console.error('Save error:', error);
      toast({
        title: "Error",
        description: "Failed to save changes. Please try again.",
        variant: "destructive"
      });
      return;
    }

    setProgress(data.progress as ProgressData);
    toast({
      title: "Success",
      description: "Progress updated successfully"
    });
    setIsEditing(false);
    setEditPassword("");
  };

  const handleSendFeedback = async () => {
    if (!feedback.trim()) {
      toast({
        title: "Error",
        description: "Please enter some feedback before sending",
        variant: "destructive"
      });
      return;
    }
    if (!feedbackEmail.trim() || !/\S+@\S+\.\S+/.test(feedbackEmail)) {
      toast({
        title: "Error",
        description: "Please enter a valid email address",
        variant: "destructive"
      });
      return;
    }
    setIsSendingFeedback(true);
    try {
      const {
        data: secrets
      } = await supabase.functions.invoke('get-emailjs-config');
      await emailjs.send(secrets.serviceId, secrets.templateId, {
        from_name: "Progress Page Feedback",
        message: feedback,
        from_email: feedbackEmail,
        to_email: "mymontage.video@gmail.com",
        to_name: "MyMontage Team",
        reply_to: feedbackEmail
      }, secrets.publicKey);
      toast({
        title: "Success",
        description: "Your feedback has been sent successfully!"
      });
      setFeedback("");
      setFeedbackEmail("");
    } catch (error) {
      console.error('Error sending feedback:', error);
      toast({
        title: "Error",
        description: "Failed to send feedback. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSendingFeedback(false);
    }
  };
  const calculateProgress = () => {
    if (!progress) return 0;
    const steps = [progress.order_submitted, progress.planning_form_complete, progress.started_draft_1, progress.completed_draft_1, progress.completed_draft_2, progress.completed_final_draft, progress.payment_received];
    const completed = steps.filter(Boolean).length;
    return completed / steps.length * 100;
  };
  const toggleStep = (step: keyof ProgressData) => {
    if (!progress || typeof progress[step] !== 'boolean') return;
    setProgress({
      ...progress,
      [step]: !progress[step]
    });
  };
  if (!progress) {
    return (
      <div className="max-w-6xl mx-auto p-8 bg-navy-50 rounded-lg shadow-lg">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-navy-600" />
          <span className="ml-2 text-navy-600">Loading progress...</span>
        </div>
      </div>
    );
  }
  return <div className="max-w-6xl mx-auto p-8 bg-navy-50 rounded-lg shadow-lg">
      <h1 className="text-4xl font-bold text-navy-800 mb-8">Welcome {familyName}!</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h2 className="text-2xl font-semibold text-navy-700 mb-4">Progress Overview</h2>
          <Progress value={calculateProgress()} className="mb-6" />
          
          <Button onClick={() => navigate("/planning-form")} variant="default" className="mb-6">
            Go to Planning Form
          </Button>
        </div>

        <div className="relative">
          <div className="absolute left-4 top-0 h-full w-0.5 bg-navy-200"></div>
          <div className="space-y-8 relative">
            {[{
            key: 'order_submitted',
            label: 'Order Submitted'
          }, {
            key: 'planning_form_complete',
            label: 'Planning Form Complete'
          }, {
            key: 'started_draft_1',
            label: 'Started Draft 1'
          }, {
            key: 'completed_draft_1',
            label: 'Completed Draft 1'
          }, {
            key: 'completed_draft_2',
            label: 'Completed Draft 2'
          }, {
            key: 'completed_final_draft',
            label: 'Completed Final Draft'
          }, {
            key: 'payment_received',
            label: 'Payment Received'
          }].map(({
            key,
            label
          }) => <div key={key} className="flex items-center gap-4 pl-8 relative">
                <div className={`absolute left-3 w-3 h-3 rounded-full ${progress[key as keyof ProgressData] ? 'bg-navy-600' : 'bg-navy-200'}`} style={{
              transform: 'translateX(-50%)'
            }}></div>
                <Checkbox id={key} checked={progress[key as keyof ProgressData] as boolean} onCheckedChange={() => isEditing && toggleStep(key as keyof ProgressData)} disabled={!isEditing} />
                <label htmlFor={key} className="text-navy-700 flex-grow">
                  {label}
                </label>
              </div>)}
          </div>
        </div>
      </div>

      <div className="mt-8 space-y-4">
        <h3 className="text-xl font-semibold text-navy-700">Upload Your Photos</h3>
        <div className="bg-white p-6 rounded-lg border border-navy-200 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="bg-navy-100 p-3 rounded-full">
              <ExternalLink className="w-6 h-6 text-navy-600" />
            </div>
            <div>
              <h4 className="text-lg font-medium text-navy-800 mb-2">Upload Your Photos to Google Drive</h4>
              <p className="text-navy-600 mb-4">
                Please also create folders inside of the main folder for each of your sections. Inside of those folders, please label each photo with a number, 1 going first and 100 going last in the timeline of the montage for example.
              </p>
              <p className="text-navy-600 mb-4">
                Please upload your photos to our shared Google Drive folder. This helps us manage large files more efficiently.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={() => window.open(googleDriveUrl, '_blank')} className="flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Open Google Drive Folder
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 space-y-4">
        <h3 className="text-xl font-semibold text-navy-700">Send us Feedback</h3>
        <Input type="email" value={feedbackEmail} onChange={e => setFeedbackEmail(e.target.value)} placeholder="Your email address" required />
        <Textarea value={feedback} onChange={e => setFeedback(e.target.value)} placeholder="Type your feedback here..." className="min-h-[100px]" />
        <Button onClick={handleSendFeedback} disabled={isSendingFeedback} className="flex items-center gap-2">
          {isSendingFeedback ? <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Sending...
            </> : <>
              <Send className="w-4 h-4" />
              Send Feedback
            </>}
        </Button>
      </div>

      {/* Floating Edit Button */}
      <div className="fixed bottom-8 right-8">
        <AnimatePresence>
          {isEditOpen && <motion.div initial={{
          width: 0,
          opacity: 0
        }} animate={{
          width: "300px",
          opacity: 1
        }} exit={{
          width: 0,
          opacity: 0
        }} className="absolute right-full mr-4 flex items-center bg-white rounded-lg shadow-lg overflow-hidden">
              <Input type="password" placeholder="Enter password" value={password} onChange={e => setPassword(e.target.value)} className="border-none px-4 py-2 w-full" />
              <Button onClick={handleEditClick} variant="ghost" size="icon" className="shrink-0">
                <Save className="w-4 h-4" />
              </Button>
            </motion.div>}
        </AnimatePresence>

        {isEditing ? <Button onClick={handleSave} size="icon" className="rounded-full w-12 h-12 shadow-lg">
            <Save className="w-6 h-6" />
          </Button> : <Button onClick={() => setIsEditOpen(!isEditOpen)} size="icon" className="rounded-full w-12 h-12 shadow-lg">
            <Pencil className="w-6 h-6" />
          </Button>}
      </div>
    </div>;
};